import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Stars, Sparkles } from 'lucide-react';

// Magical floating particles component
const MagicalParticles = () => {
  const particles = useMemo(() =>
    [...Array(50)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5,
      color: ['#f59e0b', '#14b8a6', '#6b4c9a', '#ec4899', '#fcd34d'][Math.floor(Math.random() * 5)]
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            opacity: [0.2, 0.8, 0.2],
            scale: [1, 1.5, 1]
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      ))}
    </div>
  );
};

// Animated stars background
const StarField = () => {
  const stars = useMemo(() =>
    [...Array(100)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 0.5,
      duration: Math.random() * 3 + 2
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute bg-white rounded-full"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: star.duration,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
      ))}
    </div>
  );
};

// Newsletter Modal
const NewsletterModal = ({ isOpen, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(email);
    setEmail('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="relative max-w-md w-full overflow-hidden rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Magical gradient border */}
            <div className="absolute inset-0 p-[2px] rounded-3xl bg-gradient-to-br from-amber-400 via-purple-500 to-teal-400">
              <div className="w-full h-full bg-[#0f0f2a] rounded-3xl" />
            </div>

            <div className="relative p-8 z-10">
              {/* Sparkles decoration */}
              <motion.div
                className="absolute top-4 right-12"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              >
                <Stars className="w-8 h-8 text-amber-400/50" />
              </motion.div>

              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="font-display text-3xl font-bold gradient-text-gold mb-3 text-center">
                  Join the Adventure!
                </h3>
                <p className="text-gray-400 text-center mb-6">
                  Get exclusive mysteries, behind-the-scenes content, and magical surprises!
                </p>
              </motion.div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-amber-400 transition-colors" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:border-amber-400/50 focus:ring-2 focus:ring-amber-400/20 outline-none transition-all"
                    required
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 px-6 rounded-2xl font-bold text-lg relative overflow-hidden group"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)'
                  }}
                >
                  <span className="relative z-10 text-[#0a0a1a]">Subscribe Now</span>
                  <motion.div
                    className="absolute inset-0 bg-white/30"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Animated title letter
const AnimatedLetter = ({ letter, index }) => (
  <motion.span
    className="inline-block cursor-default"
    initial={{ y: 50, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{
      delay: 0.5 + index * 0.05,
      type: 'spring',
      stiffness: 200,
      damping: 15
    }}
    whileHover={{
      y: -10,
      color: '#f59e0b',
      textShadow: '0 0 30px rgba(245, 158, 11, 0.8)',
      transition: { duration: 0.2 }
    }}
  >
    {letter === ' ' ? '\u00A0' : letter}
  </motion.span>
);

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPrompt, setShowPrompt] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const slides = [
    '/images/slide1.svg',
    '/images/slide2.svg',
    '/images/slide3.svg'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const handleSubscribe = (email) => {
    console.log('Subscribed with:', email);
    setShowModal(false);
    setShowPrompt(false);
  };

  return (
    <div className="h-screen w-full relative overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 hero-gradient" />

        {/* Animated background images */}
        {slides.map((slide, index) => (
          <motion.div
            key={slide}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${slide})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{
              opacity: index === currentSlide ? 0.3 : 0,
              scale: index === currentSlide ? 1 : 1.1
            }}
            transition={{ duration: 2, ease: 'easeInOut' }}
          />
        ))}

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a1a]/60 via-[#0a0a1a]/40 to-[#0a0a1a]/90" />

        {/* Star field */}
        <StarField />

        {/* Magical particles */}
        <MagicalParticles />

        {/* Radial glows */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(107, 76, 154, 0.4) 0%, transparent 60%)',
            filter: 'blur(60px)'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(20, 184, 166, 0.3) 0%, transparent 60%)',
            filter: 'blur(60px)'
          }}
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      {/* Main content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="max-w-6xl mx-auto px-6 text-center">
          {/* Decorative top element */}
          <motion.div
            className="flex items-center justify-center gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <motion.div
              className="h-[1px] w-20 bg-gradient-to-r from-transparent to-amber-400"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            />
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            >
              <Sparkles className="w-6 h-6 text-amber-400" />
            </motion.div>
            <motion.div
              className="h-[1px] w-20 bg-gradient-to-l from-transparent to-amber-400"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            />
          </motion.div>

          {/* Main title */}
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black mb-6 tracking-wide whitespace-nowrap">
            {'NED AND NANCY'.split('').map((letter, index) => (
              <AnimatedLetter key={index} letter={letter} index={index} />
            ))}
          </h1>

          {/* Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="relative mb-12"
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-light tracking-[0.2em] text-gray-300">
              <span className="gradient-text">HUMOROUS</span>
              <span className="text-white/60 mx-3">&</span>
              <span className="gradient-text-gold">MYSTERIOUS</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-400 mt-4 font-serif italic">
              Children's Adventures That Spark Wonder
            </p>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(245, 158, 11, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="btn-magic text-lg px-10 py-4"
              onClick={() => document.getElementById('books')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore The Books
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 rounded-full font-semibold text-lg border-2 border-white/20 text-white hover:border-teal-400/50 hover:text-teal-400 transition-all duration-300"
              onClick={() => document.getElementById('podcast')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Listen to Podcast
            </motion.button>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
          >
            <motion.div
              className="flex flex-col items-center gap-2 cursor-pointer"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              onClick={() => document.getElementById('books')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="text-xs text-gray-500 uppercase tracking-widest">Scroll to Explore</span>
              <div className="w-6 h-10 rounded-full border-2 border-gray-600 flex items-start justify-center p-2">
                <motion.div
                  className="w-1.5 h-1.5 rounded-full bg-amber-400"
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 right-10 flex gap-2">
        {slides.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-amber-400 w-8' : 'bg-white/30 hover:bg-white/50'
            }`}
            whileHover={{ scale: 1.2 }}
          />
        ))}
      </div>

      {/* Newsletter Slide-in Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 3 }}
            className="fixed right-0 top-1/2 -translate-y-1/2 z-40 max-w-sm"
          >
            {/* Gradient border wrapper */}
            <div className="relative p-[2px] rounded-l-3xl bg-gradient-to-br from-amber-400 via-purple-500 to-teal-400">
              <div className="bg-[#0f0f2a] rounded-l-3xl p-6 pr-8">
                <button
                  onClick={() => setShowPrompt(false)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <motion.div
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Stars className="w-8 h-8 text-amber-400" />
                  </motion.div>
                  <h3 className="font-display text-xl font-bold text-white">
                    Join the Adventure!
                  </h3>
                </div>

                <p className="text-gray-400 text-sm mb-6">
                  Sign up for the Official Ned and Nancy Newsletter for exclusive mysteries!
                </p>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowModal(true)}
                    className="flex-1 py-3 px-6 rounded-xl font-bold text-[#0a0a1a]"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)'
                    }}
                  >
                    Yes!
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowPrompt(false)}
                    className="flex-1 py-3 px-6 rounded-xl font-bold bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    Later
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <NewsletterModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubscribe}
      />
    </div>
  );
};

export default Hero;
